import React, { useContext } from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Grid,
} from "@mui/material";
import { COVERAGE_FORM } from "@platform/rfp-core";
import { TextField, Typography } from "@platform/design-system";

import { CommissionSection, SectionTitle } from "../../components";
import { RFP_ACTION_TYPES } from "../../constants";
import { RfpContext } from "../../contexts/rfpContext";
import CoverageButtonWrapper from "../components/coverageButtonWrapper";
import CommentsField from "../components/commentsField";

import { createValidationHandlers } from "./coveragesValidations";

const {
  ADDITIONAL_NOTES,
  ALTERNATE_PLANS,
  CURRENT_COMMISSION,
  REQUESTED_COMMISSION,
  ADA,
  INCLUDED,
  NOT_INCLUDED,
  ENROLLED_W2_EMPLOYEES,
  PLANS,
  PLAN_TYPE,
  SINGLE_PLAN,
  NO_PLAN,
} = COVERAGE_FORM;

const { UPDATE_SELECTED_COVERAGE } = RFP_ACTION_TYPES;

export const initialFMLACoveragesDetails = {
  [PLAN_TYPE]: SINGLE_PLAN,
  [PLANS]: [
    {
      [ENROLLED_W2_EMPLOYEES]: "",
      [ADA]: "",
      [CURRENT_COMMISSION]: "",
      [REQUESTED_COMMISSION]: "",
      [ADDITIONAL_NOTES]: "",
      [ALTERNATE_PLANS]: "",
      name: SINGLE_PLAN,
    },
  ],
};

const FamilyMedicalLeaveAdministration = ({
  coverage,
  nextCoverage,
  isLastCoverage,
  handleNext,
  handleBack,
  prevCoverage,
  isFirstCoverage,
}) => {
  const { setRfp } = useContext(RfpContext);

  const handleFormChange = (updatedCoverage, currentPlanKey, fieldKey) => {
    setRfp({
      type: UPDATE_SELECTED_COVERAGE,
      value: updatedCoverage,
      currentPlanKey,
      fieldKey,
    });
  };

  return (
    <>
      <FamilyMedicalLeaveAdministrationForm
        coverage={coverage}
        onFormChange={handleFormChange}
      />
      <CoverageButtonWrapper
        displayBackButton={!isFirstCoverage}
        displayNextButton={!isLastCoverage}
        backButtonName={prevCoverage?.name}
        nextButtonName={nextCoverage?.name}
        handleBackAction={handleBack}
        handleNextAction={handleNext}
      />
    </>
  );
};

export const FamilyMedicalLeaveAdministrationForm = ({
  coverage,
  onFormChange,
}) => {
  const isCurrentCoverage = coverage?.isCurrentCoverage;
  const currentPlan = coverage?.details[PLANS][0];
  const currentPlanErrors = currentPlan.errors || {};

  const handlePlanFormChange = ({ name, value }) => {
    const localPlan = {
      ...currentPlan,
      [name]: value,
      displayName: NO_PLAN,
    };
    const payload = {
      ...coverage,
      details: { ...coverage.details, [PLANS]: [localPlan] },
    };

    onFormChange(payload, 0, name);
  };

  // Create validation handlers using common utilities
  const { handlePlanFormChangeWithValidation, handlePreviousFieldsValidation } =
    createValidationHandlers({
      currentPlan,
      coverage,
      onFormChange,
      isCurrentCoverage,
      activeTab: 0,
    });

  return (
    <>
      <SectionTitle>W-2 Employees</SectionTitle>
      <Grid container spacing={2} px={2}>
        <Grid size={{ xs: 12, md: 9 }}>
          <TextField
            autoComplete="off"
            fullWidth
            name={ENROLLED_W2_EMPLOYEES}
            error={!!currentPlanErrors[ENROLLED_W2_EMPLOYEES]}
            value={currentPlan[ENROLLED_W2_EMPLOYEES] || ""}
            onChange={(e) => {
              handlePlanFormChange(e.target);
            }}
            onFocus={() => {
              handlePreviousFieldsValidation(ENROLLED_W2_EMPLOYEES);
            }}
            onBlur={(e) => {
              handlePlanFormChangeWithValidation(e.target);
            }}
            required
            label="Number of W-2 employees confirmed with zip codes"
          />
          {!!currentPlanErrors[ENROLLED_W2_EMPLOYEES] && (
            <Typography variant="caption" color="danger">
              {currentPlanErrors[ENROLLED_W2_EMPLOYEES]}
            </Typography>
          )}
        </Grid>
      </Grid>

      <SectionTitle>American with Disabilities Act (ADA)</SectionTitle>
      <Box pl={2} pr={2}>
        <FormControl
          onFocus={() => {
            handlePreviousFieldsValidation(ADA);
          }}
        >
          <FormLabel id="american-disabilities-act-label" required>
            Select an Option
          </FormLabel>
          <RadioGroup
            value={currentPlan[ADA]}
            row
            aria-labelledby="american-disabilities-act-label"
            name={ADA}
            onChange={(e) => {
              handlePlanFormChangeWithValidation(e.target);
            }}
          >
            <FormControlLabel
              value={INCLUDED}
              control={<Radio />}
              label={INCLUDED}
            />
            <FormControlLabel
              value={NOT_INCLUDED}
              control={<Radio />}
              label={NOT_INCLUDED}
            />
          </RadioGroup>
        </FormControl>
        <Box>
          {!!currentPlanErrors[ADA] && (
            <Typography variant="caption" color="danger">
              {currentPlanErrors[ADA]}
            </Typography>
          )}
        </Box>
      </Box>

      <CommissionSection
        handlePlanFormChange={handlePlanFormChange}
        handlePlanFormChangeWithValidation={handlePlanFormChangeWithValidation}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
        currentPlanErrors={currentPlanErrors}
        currentPlan={currentPlan}
        isCurrentCoverage={isCurrentCoverage}
      />
      <CommentsField
        currentPlan={currentPlan}
        onChange={(e) => {
          handlePlanFormChange(e.target);
        }}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
      />
    </>
  );
};

export default FamilyMedicalLeaveAdministration;
